import React, { useEffect, useState } from 'react';
import { Link } from '@remix-run/react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '~/components/ui/button';
import { AccentBorder } from '~/components/ui/accent-border';
import { heroCascade } from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

interface HeroSectionProps {
  className?: string;
}

const HERO_IMAGES = [
  '/images/hero/hero-1.jpg',
  '/images/hero/hero-2.jpg',
  '/images/hero/hero-3.jpg',
  '/images/hero/hero-4.jpg',
  '/images/hero/hero-5.jpg',
];

const CROSSFADE_INTERVAL = 5000; // 5 seconds

export function HeroSection({ className = '' }: HeroSectionProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const { getVariants } = useAnimationState();

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    // Set loaded state for animations
    setIsLoaded(true);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Image rotation effect
  useEffect(() => {
    if (reduceMotion) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % HERO_IMAGES.length);
    }, CROSSFADE_INTERVAL);

    return () => clearInterval(interval);
  }, [reduceMotion]);

  // Simple parallax effect using scroll position
  useEffect(() => {
    if (reduceMotion) return;

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [reduceMotion]);

  return (
    <section className={`relative min-h-screen flex items-center overflow-hidden -mt-[72px] pt-[72px] ${className}`}>
      {/* Background Images with Cross-fade and Parallax */}
      <motion.div
        className="absolute inset-0 z-0 -top-[5%] h-[110%] opacity-0"
        style={{
          transform: reduceMotion ? 'none' : `translateY(${scrollY * 0.15}px)`,
          willChange: 'transform',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        {HERO_IMAGES.map((image, index) => (
          <img
            key={image}
            src={image}
            alt={`Lumora hero background ${index + 1}`}
            className={`absolute inset-0 object-cover object-top w-full h-full transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
        <div className="absolute inset-0 bg-gradient-hero" />
      </motion.div>

      {/* Content */}
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <AccentBorder variant="accent" position="top" className="max-w-3xl pt-8 overflow-visible">
          <div>
            <motion.p
              className="text-sm uppercase tracking-[0.3em] text-lumora-gold mb-4 font-medium opacity-0"
              style={{ textShadow: '0 2px 28px rgba(0, 0, 0, 0.6), 0 4px 16px rgba(0, 0, 0, 0.45)' }}
              custom={0}
              initial="hidden"
              animate="visible"
              variants={getVariants(heroCascade)}
            >
              An Epic Fantasy RPG
            </motion.p>
            <motion.h1
              className="font-fantasy text-6xl md:text-8xl font-bold text-white mb-2 tracking-tight opacity-0"
              style={{ textShadow: '0 2px 32px rgba(0, 0, 0, 0.7), 0 4px 20px rgba(0, 0, 0, 0.55)' }}
              custom={1}
              initial="hidden"
              animate="visible"
              variants={getVariants(heroCascade)}
            >
              Lumora
            </motion.h1>
            <motion.div
              className="relative mb-6 opacity-0"
              style={{ filter: 'drop-shadow(0 2px 32px rgba(0, 0, 0, 0.7)) drop-shadow(0 4px 20px rgba(0, 0, 0, 0.55))' }}
              custom={2}
              initial="hidden"
              animate="visible"
              variants={getVariants(heroCascade)}
            >
              <span className="block bg-gradient-accent-mobile md:bg-gradient-accent bg-clip-text text-transparent font-fantasy text-6xl md:text-8xl font-bold tracking-tight">
                The First Dawn
              </span>
            </motion.div>
            <motion.p
              className="font-body text-xl md:text-2xl text-gray-100 max-w-2xl mb-12 sm:mb-8 leading-relaxed opacity-0"
              style={{ textShadow: '0 2px 28px rgba(0, 0, 0, 0.6), 0 4px 16px rgba(0, 0, 0, 0.45)' }}
              custom={3}
              initial="hidden"
              animate="visible"
              variants={getVariants(heroCascade)}
            >
              Magic is reawakening. Choose a faction and shape the fate of Lumora.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 opacity-0"
              custom={4}
              initial="hidden"
              animate="visible"
              variants={getVariants(heroCascade)}
            >
              <Button
                size="lg"
                variant="accent"
                asChild
              >
                <Link to="/about">
                  Learn More
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </AccentBorder>
      </div>

      {/* Scroll Indicator */}
      <ScrollIndicator />
    </section>
  );
}

function ScrollIndicator() {
  return (
    <motion.div
      className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      animate={{
        y: [0, 10, 0],
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      <ChevronDown className="w-8 h-8 text-white/50" />
    </motion.div>
  );
}